import { useAuthStore } from '@/stores/authStore';
import { useTenantStore } from '@/stores/tenantStore';

/**
 * Resolves the tenant id a page should build its `/api/tenants/{tenantId}/…`
 * URLs from. This MUST mirror the axios Tenant-Id interceptor
 * (`src/api/axiosClient.ts` lines 49-52) EXACTLY so the URL path and the
 * `X-Tenant-Id` header can never disagree.
 *
 * Why this exists (ADR-008 / ADR-009 impersonation):
 *   A platformAdmin is HOMED in the reserved platform tenant
 *   (`PlatformSystemAdmin`), so `user.tenantId` is ALWAYS that platform tenant
 *   — never the customer tenant they are currently administering. They pick
 *   the working tenant in the Header dropdown
 *   (`useTenantStore.selectedTenantId`), and the axios interceptor sends that
 *   selection as `X-Tenant-Id`. A page that builds its URL from
 *   `user.tenantId` instead would send the path for the EMPTY platform tenant
 *   while the header carries the selected tenant — the backend trusts the
 *   path, so every tenant-scoped page would silently operate on the wrong
 *   (empty platform) tenant. Reading the effective tenant from this hook keeps
 *   path and header in lockstep.
 *
 * Tenant admins have no picker — they only ever act on their own tenant, so
 * the function falls through to `user.tenantId`.
 */
export function useEffectiveTenantId(): string {
  const user = useAuthStore((s) => s.user);
  const selectedTenantId = useTenantStore((s) => s.selectedTenantId);

  if (user?.role === 'PlatformAdmin') {
    return selectedTenantId ?? user?.tenantId ?? '';
  }
  return user?.tenantId ?? '';
}
